import { MeetingDurationType } from '@web/components/Filters/Elements/MeetingDurationType';
import { MeetingParticipantsType } from '@web/components/Filters/Elements/MeetingParticipantsType';
import { MeetingType } from '@web/components/Filters/Elements/MeetingType';
import { FILTER_PERIOD_OPTIONS } from '@web/config';
import { getDateRangeForPeriod } from '@web/utility/periodDateRange';
import { ApplicationEnum, MeetEventActionEnum, PerformanceReviewSectionsEnum } from '@workalizer/shared';
import { format, isValid, parse } from 'date-fns';
import { useLocation } from 'react-router-dom';
export function parseEnum(queryParams, key, enumType) {
    var value = queryParams.get(key);
    if (value && Object.values(enumType).includes(value)) {
        return value;
    }
    return null;
}
export function parseNumber(queryParams, key) {
    var orgUnit = queryParams.get(key);
    if (orgUnit) {
        return parseInt(orgUnit, 10);
    }
    return null;
}
export function parsePeriod(queryParams, key) {
    var period = queryParams.get(key);
    var allowedPeriods = FILTER_PERIOD_OPTIONS.map(function (o) { return o.id; });
    if (period && allowedPeriods.includes(period)) {
        return period;
    }
    return null;
}
export function parseDate(queryParams, key) {
    var date = queryParams.get(key);
    if (date) {
        var parsedDate = parse(date, 'yyyy-MM-dd', new Date());
        if (isValid(parsedDate)) {
            return date;
        }
    }
    return null;
}
export function getDefaultPeriodForPath(_pathname) {
    return 'month';
}
export function useFilters() {
    var _a;
    var location = useLocation();
    var queryParams = new URLSearchParams(location.search);
    var dateFrom = parseDate(queryParams, 'datefrom');
    var dateTo = parseDate(queryParams, 'dateto');
    var periodFromUrl = parsePeriod(queryParams, 'period');
    var defaultPeriod = getDefaultPeriodForPath(location.pathname);
    var period = periodFromUrl || defaultPeriod;
    var _b = getDateRangeForPeriod(period), defaultFrom = _b.dateFrom, defaultTo = _b.dateTo;
    var defaultDateFrom = format(defaultFrom, 'yyyy-MM-dd');
    var defaultDateTo = format(defaultTo, 'yyyy-MM-dd');
    var defaultDriveTimelineDate = format(new Date(), 'yyyy-MM-dd');
    return {
        orgUnit: parseNumber(queryParams, 'orgunit'),
        user: parseNumber(queryParams, 'user'),
        period: period,
        dateFrom: dateFrom !== null && dateFrom !== void 0 ? dateFrom : defaultDateFrom,
        dateTo: dateTo !== null && dateTo !== void 0 ? dateTo : defaultDateTo,
        app: parseEnum(queryParams, 'app', ApplicationEnum),
        page: parseNumber(queryParams, 'page'),
        step: parseNumber(queryParams, 'step'),
        docSearch: queryParams.get('docSearch') || '',
        driveSearchType: queryParams.get('driveSearchType') || null,
        driveSearchAction: queryParams.get('driveSearchAction') || null,
        driveSearchSubAction: queryParams.has('driveSearchSubAction')
            ? queryParams.get('driveSearchSubAction') || null
            : undefined,
        driveSearchTitle: queryParams.get('driveSearchTitle') || '',
        title: queryParams.get('title') || '',
        durationHours: parseNumber(queryParams, 'durationHours'),
        durationMinutes: parseNumber(queryParams, 'durationMinutes'),
        meetingDurationType: parseEnum(queryParams, 'meetingDurationType', MeetingDurationType),
        meetingParticipantsType: parseEnum(queryParams, 'meetingParticipantsType', MeetingParticipantsType),
        meetingType: parseEnum(queryParams, 'meetingType', MeetingType),
        meetEventAction: parseEnum(queryParams, 'meetEventAction', MeetEventActionEnum),
        itemId: parseNumber(queryParams, 'itemId'),
        activityLabel: parseNumber(queryParams, 'activityLabel'),
        pageSection: parseEnum(queryParams, 'pageSection', PerformanceReviewSectionsEnum),
        hasInitialSearch: queryParams.get('hasInitialSearch') === 'true',
        scrollY: parseNumber(queryParams, 'scrollY'),
        driveTimelineDate: (_a = parseDate(queryParams, 'driveTimelineDate')) !== null && _a !== void 0 ? _a : defaultDriveTimelineDate,
        driveHeatmapDate: parseDate(queryParams, 'driveHeatmapDate'),
        documentEventsUser: parseNumber(queryParams, 'documentEventsUser'),
        documentEventsAction: queryParams.get('documentEventsAction') || null,
        documentEventsSubAction: queryParams.has('documentEventsSubAction')
            ? queryParams.get('documentEventsSubAction') || null
            : undefined
    };
}
export function filtersToQuery(filters, existingSearch) {
    var _a, _b;
    var query = new URLSearchParams();
    if (filters.orgUnit) {
        query.set('orgunit', filters.orgUnit.toString());
    }
    if (filters.user) {
        query.set('user', filters.user.toString());
    }
    if (filters.period) {
        query.set('period', filters.period);
    }
    if (filters.dateFrom) {
        query.set('datefrom', format(filters.dateFrom, 'yyyy-MM-dd'));
    }
    if (filters.dateTo) {
        query.set('dateto', format(filters.dateTo, 'yyyy-MM-dd'));
    }
    if (filters.app) {
        query.set('app', filters.app.toString());
    }
    if (filters.page) {
        query.set('page', filters.page.toString());
    }
    if (filters.step) {
        query.set('step', filters.step.toString());
    }
    if (filters.docSearch) {
        query.set('docSearch', filters.docSearch);
    }
    if (filters.title) {
        query.set('title', filters.title);
    }
    if (filters.durationMinutes) {
        query.set('durationMinutes', ((_a = filters.durationMinutes) === null || _a === void 0 ? void 0 : _a.toString()) || '');
    }
    if (filters.durationHours) {
        query.set('durationHours', ((_b = filters.durationHours) === null || _b === void 0 ? void 0 : _b.toString()) || '');
    }
    if (filters.meetingDurationType) {
        query.set('meetingDurationType', filters.meetingDurationType);
    }
    if (filters.meetingParticipantsType) {
        query.set('meetingParticipantsType', filters.meetingParticipantsType);
    }
    if (filters.meetingType) {
        query.set('meetingType', filters.meetingType);
    }
    if (filters.meetEventAction) {
        query.set('meetEventAction', filters.meetEventAction);
    }
    if (filters.itemId) {
        query.set('itemId', filters.itemId.toString());
    }
    if (filters.driveSearchType) {
        query.set('driveSearchType', filters.driveSearchType);
    }
    if (filters.driveSearchAction) {
        query.set('driveSearchAction', filters.driveSearchAction);
    }
    if ('driveSearchSubAction' in filters &&
        filters.driveSearchSubAction !== undefined) {
        if (filters.driveSearchSubAction !== null) {
            query.set('driveSearchSubAction', filters.driveSearchSubAction);
        }
        else {
            query.set('driveSearchSubAction', '');
        }
    }
    if (filters.driveSearchTitle) {
        query.set('driveSearchTitle', filters.driveSearchTitle);
    }
    if (filters.activityLabel) {
        query.set('activityLabel', filters.activityLabel.toString());
    }
    if (filters.pageSection) {
        query.set('pageSection', filters.pageSection);
    }
    if (filters.hasInitialSearch === true) {
        query.set('hasInitialSearch', 'true');
    }
    if (filters.scrollY != null) {
        query.set('scrollY', String(Math.round(filters.scrollY)));
    }
    if (filters.driveTimelineDate) {
        query.set('driveTimelineDate', format(filters.driveTimelineDate, 'yyyy-MM-dd'));
    }
    if (filters.driveHeatmapDate) {
        query.set('driveHeatmapDate', format(filters.driveHeatmapDate, 'yyyy-MM-dd'));
    }
    if (filters.documentEventsUser) {
        query.set('documentEventsUser', filters.documentEventsUser.toString());
    }
    if (filters.documentEventsAction) {
        query.set('documentEventsAction', filters.documentEventsAction);
    }
    if (filters.documentEventsSubAction !== undefined) {
        if (filters.documentEventsSubAction) {
            query.set('documentEventsSubAction', filters.documentEventsSubAction);
        }
    }
    return query.toString();
}
